import React, { useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { withTranslation } from 'react-i18next'

// Statuses where the kitchen is still on the hook for the food. Once an order
// is delivered or cancelled the countdown is history, not something to tick.
const LIVE_STATUSES = ['ACCEPTED', 'ASSIGNED', 'PICKED']

const formatDuration = totalSeconds => {
  const seconds = Math.max(0, Math.floor(totalSeconds))
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
}

const formatClock = value =>
  new Date(value).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  })

const parseDate = value => {
  if (!value) return null
  const parsed = new Date(value).getTime()
  return Number.isNaN(parsed) ? null : parsed
}

// This admin's theme inverts the usual MUI palette: text.primary is #fff and
// text.secondary is #212121 (see utils/theme.js). On a white table row that
// makes text.primary invisible, so readable text here uses text.secondary and
// muted text uses text.disabled.
const LABEL_COLOR = 'text.disabled'
const VALUE_COLOR = 'text.secondary'

const Line = ({ label, value, color, bold }) => (
  <Typography
    variant="caption"
    display="block"
    sx={{ lineHeight: 1.4, whiteSpace: 'normal', wordBreak: 'break-word' }}>
    <Box component="span" sx={{ color: LABEL_COLOR }}>
      {label}:
    </Box>{' '}
    <Box
      component="span"
      sx={{ color: color || VALUE_COLOR, fontWeight: bold ? 700 : 400 }}>
      {value}
    </Box>
  </Typography>
)

/**
 * The three timings dispatch needs on an accepted order: when the store took
 * it, how long the kitchen has left, and how far past due it has run.
 *
 * `acceptedAt` is stamped by the server on both accept paths — the store app's
 * acceptOrder and the admin's own updateStatus — so it is the reliable record
 * of when the kitchen took the order.
 *
 * `preparationTime` is separate: it is the absolute moment the food is due, set
 * from the minutes picked at accept time — by the kitchen in the store app, or
 * by an admin in the accept dialog here. An order without one (scheduled
 * orders) shows the accepted time alone rather than a bogus 00:00 countdown.
 */
const PrepTimeCountdown = ({ order, t }) => {
  const { orderStatus, preparationTime, acceptedAt } = order || {}
  const isLive = LIVE_STATUSES.includes(orderStatus)
  const dueAt = parseDate(preparationTime)
  const acceptedAtMs = parseDate(acceptedAt)
  const isCountingDown = isLive && dueAt !== null

  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!isCountingDown) return undefined
    // Re-read the clock rather than decrementing a counter, so a backgrounded
    // tab that throttles timers still shows the correct remaining time.
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [isCountingDown])

  // Nothing accepted and nothing pending: no timing to report at all.
  if (!isCountingDown && acceptedAtMs === null) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    )
  }

  const acceptedLine =
    acceptedAtMs !== null ? (
      <Line label={t('AcceptedAt')} value={formatClock(acceptedAtMs)} bold />
    ) : null

  if (!isCountingDown) {
    return <Box sx={{ overflow: 'visible' }}>{acceptedLine}</Box>
  }

  const remainingSeconds = (dueAt - now) / 1000
  const isOverdue = remainingSeconds <= 0
  // Under two minutes the kitchen is nearly out of time, so it reads as a
  // warning before it turns into an overdue error.
  const isTight = remainingSeconds > 0 && remainingSeconds <= 120

  return (
    <Box sx={{ overflow: 'visible', py: 0.5 }}>
      {acceptedLine}
      {isOverdue ? (
        <Line
          label={t('Overdue')}
          value={`+${formatDuration(-remainingSeconds)}`}
          color="error.main"
          bold
        />
      ) : (
        <Line
          label={t('Remaining')}
          value={formatDuration(remainingSeconds)}
          color={isTight ? 'warning.main' : 'success.main'}
          bold
        />
      )}
      <Line label={t('Ready')} value={formatClock(dueAt)} />
    </Box>
  )
}

export default withTranslation()(PrepTimeCountdown)
